import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logBuffer } from '@/services/api/log-buffer.service';
import { ErrorState } from '@/components/ui/ErrorState';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryKey: number;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, retryKey: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary:', error, errorInfo);

    logBuffer.addErrorLog({
      error_type: error.name || 'UnknownError',
      message: error.message,
      stack_trace: error.stack,
      page_url: window.location.pathname,
      browser: this.getBrowserInfo(),
      os: this.getOSInfo(),
      severity: this.determineErrorSeverity(error),
      context: { component_stack: errorInfo.componentStack, error_boundary: 'ErrorBoundary' },
    });

    this.props.onError?.(error, errorInfo);
  }

  private determineErrorSeverity(error: Error): 'low' | 'medium' | 'high' | 'critical' {
    const msg = error.message.toLowerCase();
    if (msg.includes('database') || msg.includes('auth') || msg.includes('permission')) return 'critical';
    if (msg.includes('network') || msg.includes('timeout') || msg.includes('failed to fetch')) return 'high';
    if (msg.includes('validation') || msg.includes('invalid')) return 'medium';
    return 'medium';
  }

  private getBrowserInfo(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  private getOSInfo(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Win')) return 'Windows';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS')) return 'iOS';
    return 'Unknown';
  }

  handleRetry = () => {
    this.setState(prev => ({ hasError: false, error: null, retryKey: prev.retryKey + 1 }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex items-center justify-center py-16 px-6">
          <ErrorState
            domain="golf"
            variant="crash"
            onRetry={this.handleRetry}
            onGoHome={() => { window.location.href = '/dashboard'; }}
            devError={this.state.error}
            className="w-full max-w-sm"
          />
        </div>
      );
    }

    return (
      <React.Fragment key={this.state.retryKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

function TranslatedErrorBoundary({ children, fallback, onError }: Omit<ErrorBoundaryProps, 'translations'>) {
  return (
    <ErrorBoundary fallback={fallback} onError={onError}>
      {children}
    </ErrorBoundary>
  );
}

export { ErrorBoundary, TranslatedErrorBoundary };
